import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { Menubar } from '@/types';
import styles from './PrimaryNav.module.css';

interface PrimaryNavProps {
  menuItems: Menubar[];
}

export function PrimaryNav({ menuItems }: PrimaryNavProps) {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  return (
    <nav
      className={styles.nav}
      aria-label="Primary navigation"
      role="navigation"
    >
      <div className={`container ${styles.inner}`}>
        <ul className={styles.list}>
          {menuItems.map((item) => {
            const hasItems = Boolean(item.items?.length);
            const isOpen = openMenuId === item.menuId;

            return (
              <li
                key={item.menuId}
                className={styles.menuItem}
                onMouseEnter={() => hasItems && setOpenMenuId(item.menuId)}
                onMouseLeave={() => setOpenMenuId(null)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setOpenMenuId(null);
                  }
                }}
              >
                {item.url ? (
                  <NavLink
                    to={item.url.startsWith('/') ? item.url : `/${item.url}`}
                    className={({ isActive }) =>
                      `${styles.link} ${isActive ? styles.active : ''}`
                    }
                    end
                  >
                    {item.label}
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    className={styles.link}
                    aria-expanded={hasItems ? isOpen : undefined}
                    aria-controls={hasItems ? `nav-menu-${item.menuId}` : undefined}
                    onClick={() => hasItems && setOpenMenuId(isOpen ? null : item.menuId)}
                  >
                    {item.label}
                  </button>
                )}
                {hasItems && (
                  <ul
                    id={`nav-menu-${item.menuId}`}
                    className={`${styles.submenu} ${isOpen ? styles.submenuOpen : ''}`}
                  >
                    {item.items!.map((child) => (
                      <li key={child.menuId}>
                        {child.url ? (
                          <a
                            href={child.url}
                            className={styles.submenuLink}
                            target={child.openInNewWindow ? '_blank' : undefined}
                            rel={child.openInNewWindow ? 'noopener noreferrer' : undefined}
                            onClick={(event) => {
                              setOpenMenuId(null);
                              event.currentTarget.blur();
                            }}
                          >
                            {child.label}
                          </a>
                        ) : (
                          <button type="button" className={styles.submenuLink} aria-disabled="true">
                            {child.label}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
